import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CreateCustomerDialog } from "@/components/customers/CreateCustomerDialog";
import { GatePassOptionCard } from "@/components/sales/GatePassOptionCard";
import { SaleInstallmentPlanner } from "@/components/sales/SaleInstallmentPlanner";
import { MoneyAmountField } from "@/components/sales/MoneyAmountInput";
import { SaleProductPicker } from "@/components/sales/SaleProductPicker";
import {
  ProductDealSummary,
  type ProductDealSummaryData,
} from "@/components/sales/ProductDealSummary";
import {
  buildSalePaymentsPayload,
  newSalePaymentLine,
  salePaymentLinesTotal,
  type SalePaymentLine,
} from "@/lib/sales/salePaymentLines";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import type { ProductDealType, ProductProfitRuleType } from "@/lib/catalog/dealPartners";
import { fetchProductFromApi, type ProductRecord } from "@/lib/catalog/productsStore";
import { resolveProductThumbnailUrl } from "@/lib/catalog/productThumbnail";
import { listCustomers, syncCustomersFromApi, formatCustomerDisplayName } from "@/lib/customers/customersStore";
import { listEmployees, syncEmployeesFromApi } from "@/lib/hr/employeesStore";
import { listDepartments, syncDepartmentsFromApi } from "@/lib/hr/departmentsStore";
import {
  buildInstallmentPlan,
  installmentsMatchBalance,
  type InstallmentFrequency,
  type SaleInstallmentDraft,
} from "@/lib/sales/saleInstallments";
import { formatDeliveryPayload, nowDeliveryParts } from "@/lib/sales/gatePass";
import { cn } from "@/lib/utils";
import { fetchSaleFromApi } from "@/lib/sales/salesIndexStore";
import {
  createSale,
  saleErrorMessage,
  updateSale,
  type SalePaymentMethod,
  type SaleTokenType,
} from "@/lib/sales/salesStore";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import {
  ConfigureSalePaymentDialog,
  type SalePaymentEntry,
  type SalePaymentType,
} from "@/components/sales/ConfigureSalePaymentDialog";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { format } from "date-fns";
import { ArrowLeft, Calculator, CheckCircle2, Edit, FileText, Loader2, Package, Plus, Save, Trash2, Wallet } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

type SalePaymentMode = SalePaymentType;

function money(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function SectionHeader({
  title,
  subtitle,
  icon: Icon,
  action,
}: {
  title: string;
  subtitle?: string;
  icon: ComponentType<{ className?: string }>;
  action?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-sm font-semibold text-foreground">{title}</h3>
          {subtitle ? <p className="text-xs text-muted-foreground">{subtitle}</p> : null}
        </div>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

function FieldGroup({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="space-y-3 border-t border-border/40 pt-2 first:border-t-0 first:pt-0">
      {title ? <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4> : null}
      {children}
    </div>
  );
}

function RequiredLabel({ children }: { children: ReactNode }) {
  return (
    <Label className="text-xs text-muted-foreground">
      {children} <span className="text-destructive">*</span>
    </Label>
  );
}

function dealSummaryFromProduct(p: ProductRecord): ProductDealSummaryData {
  const landed = p.landedCost ?? computeLandedCost(p.purchasePrice, p.purchaseCostLines);
  return {
    dealType: (p.dealType ?? null) as ProductDealType | null,
    profitRuleType: (p.profitRuleType ?? null) as ProductProfitRuleType | null,
    ownerDemandPrice: p.ownerDemandPrice ?? null,
    expectedSalePrice: p.expectedSalePrice ?? null,
    landedCost: landed ?? null,
    salePrice: p.salePrice ?? null,
    dealPartners: p.dealPartners ?? [],
    showroomCharge: p.showroomCharge ?? p.companyOwnerCommission ?? null,
    companyOwnerCommission: p.showroomCharge ?? p.companyOwnerCommission ?? null,
  };
}

const CreateSale = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  const [searchParams] = useSearchParams();
  const prefillProductId = searchParams.get("product_id")?.trim() ?? "";

  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "sale_can_create") || can(user, "product_can_view_prices");
  const canEdit = can(user, "sale_can_update") || can(user, "sale_can_create");
  const canSave = isEdit ? canEdit : canCreate;
  const canCreateCustomer = can(user, "customer_can_create");

  const [customerVersion, setCustomerVersion] = useState(0);
  const [employeeVersion, setEmployeeVersion] = useState(0);
  const [refsLoading, setRefsLoading] = useState(true);
  const [prefillLoading, setPrefillLoading] = useState(Boolean(prefillProductId) && !isEdit);
  const [editHydrated, setEditHydrated] = useState(() => !isEdit);
  const [editLoadError, setEditLoadError] = useState<string | null>(null);

  useEffect(() => {
    const refresh = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", refresh);
    window.addEventListener("auth_user_updated", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("auth_user_updated", refresh);
    };
  }, []);

  useEffect(() => {
    void Promise.all([
      syncCustomersFromApi(),
      syncEmployeesFromApi(),
      syncDepartmentsFromApi(),
    ])
      .then(() => {
        setCustomerVersion((v) => v + 1);
        setEmployeeVersion((v) => v + 1);
      })
      .finally(() => setRefsLoading(false));
  }, []);

  useEffect(() => {
    if (isEdit || !prefillProductId) {
      if (!isEdit) setPrefillLoading(false);
      return;
    }
    let cancelled = false;
    setPrefillLoading(true);
    void fetchProductFromApi(prefillProductId)
      .then((p) => {
        if (cancelled) return;
        if (p && (p.inventoryStatus ?? "draft") === "in_stock") {
          const pick: SaleProductPick = {
            id: String(p.id),
            name: p.name,
            salePrice: p.salePrice ?? null,
            inventoryStatus: p.inventoryStatus ?? "in_stock",
            thumbnailUrl: resolveProductThumbnailUrl({ productImages: p.productImages }),
          };
          setSelectedProduct(pick);
          setProductDeal(dealSummaryFromProduct(p));
          if (pick.salePrice != null && Number.isFinite(pick.salePrice) && pick.salePrice > 0) {
            setSaleTotal(String(pick.salePrice));
          }
        } else if (p) {
          setProductDeal(null);
          toast.error("This product is not in stock and cannot be sold.");
        }
      })
      .finally(() => {
        if (!cancelled) setPrefillLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [prefillProductId, isEdit]);

  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    setEditLoadError(null);
    setEditHydrated(false);
    void fetchSaleFromApi(editId)
      .then((sale) => {
        if (cancelled) return;
        if (!sale) {
          setEditLoadError("Sale not found.");
          return;
        }
        if (sale.is_cancelled || sale.cancelled_at || sale.void_type === "cancel" || sale.payment_status === "cancelled") {
          setEditLoadError("This sale is cancelled and cannot be edited.");
          return;
        }
        if (sale.is_returned || sale.void_type === "return" || sale.payment_status === "returned") {
          setEditLoadError("This sale was returned and cannot be edited.");
          return;
        }
        if (sale.is_installment) {
          setEditLoadError(
            "Installment sales cannot be edited from this screen. Record installment payments from the invoice, or return the sale if it must be reversed.",
          );
          return;
        }
        if (sale.product) {
          const productId = String(sale.product.id);
          setSelectedProduct({
            id: productId,
            name: sale.product.name,
            salePrice: sale.total_amount ?? null,
            inventoryStatus: "sold",
            thumbnailUrl: resolveProductThumbnailUrl(sale.product),
          });
          setProductDealLoading(true);
          void fetchProductFromApi(productId)
            .then((p) => {
              if (cancelled) return;
              setProductDeal(p ? dealSummaryFromProduct(p) : null);
            })
            .catch(() => {
              if (!cancelled) setProductDeal(null);
            })
            .finally(() => {
              if (!cancelled) setProductDealLoading(false);
            });
        } else {
          setProductDeal(null);
          setProductDealLoading(false);
        }
        setSaleCustomerId(sale.customer_id ? String(sale.customer_id) : "");
        setSaleEmployeeId(sale.employee_id ? String(sale.employee_id) : "");
        setSaleDate(sale.date?.slice(0, 10) ?? "");
        setSaleTotal(String(sale.total_amount ?? ""));
        setInstallmentEnabled(false);
        setSaleTokenType((sale.token_type as SaleTokenType) || "unconditional");
        setSalePayableDate(sale.payable_date?.slice(0, 10) ?? "");

        const receiptPayments = (sale.payments ?? []).filter((p) => {
          const type = String(p.transaction_type ?? "");
          return type === "token" || type === "sale";
        });

        if (receiptPayments.length > 0) {
          const saleTotalAmt = Number(sale.total_amount ?? 0);
          const outstanding = Number(
            sale.outstanding ?? sale.balance_outstanding ?? sale.balance_amount ?? saleTotalAmt,
          );
          const isFullySettled = saleTotalAmt > 0 && outstanding <= 0.009;
          const entries: SalePaymentEntry[] = receiptPayments.map((p, idx) => {
            const type = String(p.transaction_type ?? "");
            let paymentMode: SalePaymentType = "token";
            if (isFullySettled) {
              paymentMode = "full";
            } else if (type === "sale") {
              paymentMode = "partial";
            } else {
              paymentMode = "token";
            }
            return {
              id: String(p.id || idx + 1),
              paymentMode,
              amount: Number(p.amount) || 0,
              paymentDate: p.payment_date || sale.date?.slice(0, 10) || "",
              paymentMethod: (p.payment_method as SalePaymentMethod) || "cash",
              bankId: p.bank_id ? String(p.bank_id) : "",
              bankName: p.bank_name || p.bank?.name || "",
              transactionReferenceNo: p.transaction_reference_no || "",
              payOrderNo: p.pay_order_no || "",
              chequeNo: p.cheque_no || "",
              depositSlipNo: p.deposit_slip_no || "",
              chequeDate: p.cheque_date || "",
              tokenType: paymentMode === "token" ? ((sale.token_type as SaleTokenType) || undefined) : undefined,
              payableDate: paymentMode === "token" ? (sale.payable_date?.slice(0, 10) || undefined) : undefined,
              notes: p.notes || "",
            };
          });
          setPaymentEntries(entries);
        } else if (Number(sale.token_amount ?? 0) > 0) {
          const entry: SalePaymentEntry = {
            id: "1",
            paymentMode: (sale.balance_amount ?? 0) <= 0 ? "full" : "token",
            amount: Number(sale.token_amount ?? 0),
            paymentDate: sale.date?.slice(0, 10) || "",
            paymentMethod: (sale.payment_method as SalePaymentMethod) || "cash",
            bankId: sale.bank_id ? String(sale.bank_id) : "",
            transactionReferenceNo: sale.transaction_reference_no || "",
            payOrderNo: sale.pay_order_no || "",
            tokenType: (sale.token_type as SaleTokenType) || undefined,
            payableDate: sale.payable_date?.slice(0, 10) || undefined,
          };
          setPaymentEntries([entry]);
        } else {
          setPaymentEntries([]);
        }

        setSaleDescription(sale.description ?? "");
        setEditHydrated(true);
      })
      .catch((error) => {
        if (!cancelled) setEditLoadError(saleErrorMessage(error));
      });
    return () => {
      cancelled = true;
    };
  }, [editId]);

  const [selectedProduct, setSelectedProduct] = useState<SaleProductPick | null>(null);
  const [productDeal, setProductDeal] = useState<ProductDealSummaryData | null>(null);
  const [productDealLoading, setProductDealLoading] = useState(false);
  const productDealRequestRef = useRef(0);
  const [saleCustomerId, setSaleCustomerId] = useState("");
  const [saleEmployeeId, setSaleEmployeeId] = useState("");
  const [saleDate, setSaleDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [saleTotal, setSaleTotal] = useState("");
  const [saleTokenType, setSaleTokenType] = useState<SaleTokenType>("unconditional");
  const [salePayableDate, setSalePayableDate] = useState("");
  const [saleDescription, setSaleDescription] = useState("");
  const [saleSaving, setSaleSaving] = useState(false);
  const [gatePassEnabled, setGatePassEnabled] = useState(false);
  const [gatePassDate, setGatePassDate] = useState(() => nowDeliveryParts().date);
  const [gatePassTime, setGatePassTime] = useState(() => nowDeliveryParts().time);
  const [createCustomerOpen, setCreateCustomerOpen] = useState(false);
  const [installmentEnabled, setInstallmentEnabled] = useState(false);
  const [salePaymentType, setSalePaymentType] = useState<"cash" | "installment">("cash");
  const [latePenaltyType, setLatePenaltyType] = useState<"none" | "percentage" | "fixed">("none");
  const [latePenaltyValue, setLatePenaltyValue] = useState("");
  const [installmentMonths, setInstallmentMonths] = useState(3);
  const [installmentFirstDueDate, setInstallmentFirstDueDate] = useState("");
  const [installmentFrequency, setInstallmentFrequency] = useState<InstallmentFrequency>("monthly");
  const [installmentRows, setInstallmentRows] = useState<SaleInstallmentDraft[]>([]);
  const [downPayment, setDownPayment] = useState<number>(0);
  const installmentsManuallyEditedRef = useRef(false);

  const [paymentEntries, setPaymentEntries] = useState<SalePaymentEntry[]>([]);
  const [editingPaymentEntry, setEditingPaymentEntry] = useState<SalePaymentEntry | null>(null);
  const [pendingPaymentRemoveIndex, setPendingPaymentRemoveIndex] = useState<number | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const handleSavePaymentEntry = (entry: SalePaymentEntry) => {
    setPaymentEntries((prev) => {
      const idx = prev.findIndex((p) => p.id === entry.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = entry;
        return next;
      }
      return [...prev, entry];
    });

    if (entry.paymentMode === "installment") {
      setSalePaymentType("installment");
      setInstallmentEnabled(true);
      if (entry.installmentMonths) setInstallmentMonths(entry.installmentMonths);
      if (entry.installmentFirstDueDate) setInstallmentFirstDueDate(entry.installmentFirstDueDate);
    }

    if (entry.payableDate) setSalePayableDate(entry.payableDate);
    if (entry.tokenType) setSaleTokenType(entry.tokenType);
    if (entry.paymentDate) setSaleDate(entry.paymentDate);
  };

  const handleRemovePaymentEntry = (index: number) => {
    setPaymentEntries((prev) => prev.filter((_, i) => i !== index));
    setPendingPaymentRemoveIndex(null);
  };

  const customers = useMemo(() => listCustomers(), [customerVersion]);
  
  const salesDepartmentId = useMemo(() => {
    const depts = listDepartments();
    const salesDept = depts.find((d) => d.companyId === null && d.name === "Sales");
    return salesDept ? String(salesDept.id) : null;
  }, [employeeVersion]);

  const employees = useMemo(() => {
    return listEmployees().filter((e) => {
      const isSales = salesDepartmentId ? String(e.departmentId) === salesDepartmentId : false;
      const isCurrentlySelected = String(e.id) === saleEmployeeId;
      return isSales || isCurrentlySelected;
    });
  }, [employeeVersion, salesDepartmentId, saleEmployeeId]);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const loadProductDealSummary = (productId: string | null | undefined) => {
    const id = productId?.trim() ?? "";
    if (!id) {
      productDealRequestRef.current += 1;
      setProductDeal(null);
      setProductDealLoading(false);
      return;
    }
    const requestId = ++productDealRequestRef.current;
    setProductDealLoading(true);
    void fetchProductFromApi(id)
      .then((p) => {
        if (requestId !== productDealRequestRef.current) return;
        setProductDeal(p ? dealSummaryFromProduct(p) : null);
      })
      .catch(() => {
        if (requestId !== productDealRequestRef.current) return;
        setProductDeal(null);
      })
      .finally(() => {
        if (requestId === productDealRequestRef.current) setProductDealLoading(false);
      });
  };

  const handleProductChange = (product: SaleProductPick | null) => {
    setSelectedProduct(product);
    if (product?.salePrice != null && Number.isFinite(product.salePrice) && product.salePrice > 0) {
      setSaleTotal(String(product.salePrice));
    }
    loadProductDealSummary(product?.id);
  };

  const effectiveSaleTotal = useMemo(() => {
    const trimmed = saleTotal.trim();
    if (!trimmed) return 0;
    const v = Number(trimmed);
    if (!Number.isFinite(v) || v < 0) return 0;
    return Math.round(v * 100) / 100;
  }, [saleTotal]);

  const totalPaidAmount = useMemo(() => {
    return paymentEntries.reduce((sum, p) => sum + (Number.isFinite(p.amount) ? p.amount : 0), 0);
  }, [paymentEntries]);

  const tokenPaymentEntries = useMemo(() => {
    return paymentEntries.filter((p) => p.paymentMode === "token");
  }, [paymentEntries]);

  const tokenAmount = useMemo(() => {
    return tokenPaymentEntries.reduce((sum, p) => sum + (Number.isFinite(p.amount) ? p.amount : 0), 0);
  }, [tokenPaymentEntries]);

  const tokenEntry = useMemo(() => {
    return tokenPaymentEntries[0] ?? null;
  }, [tokenPaymentEntries]);

  const effectiveBalance = useMemo(() => {
    if (effectiveSaleTotal <= 0) return 0;
    return Math.max(0, Math.round((effectiveSaleTotal - totalPaidAmount) * 100) / 100);
  }, [effectiveSaleTotal, totalPaidAmount]);

  const primaryPaymentEntry = paymentEntries[0] ?? null;
  const isInstallmentSale = salePaymentType === "installment";

  const effectiveDownPayment = useMemo(() => {
    if (!isInstallmentSale) return 0;
    return Math.min(effectiveSaleTotal, Math.max(0, downPayment));
  }, [isInstallmentSale, effectiveSaleTotal, downPayment]);

  /** Installment schedule covers the financed remaining balance (sale price - down payment). */
  const financedAmount = useMemo(() => {
    if (!isInstallmentSale) return effectiveBalance;
    return Math.max(0, Math.round((effectiveSaleTotal - effectiveDownPayment) * 100) / 100);
  }, [isInstallmentSale, effectiveSaleTotal, effectiveDownPayment, effectiveBalance]);

  const downPaymentPaymentError = useMemo(() => {
    if (!isInstallmentSale) return null;
    if (effectiveDownPayment <= 0 && totalPaidAmount > 0) {
      return "Down payment is set to 0. Adjust down payment above if recording an upfront payment.";
    }
    if (totalPaidAmount > effectiveDownPayment + 0.02) {
      return `Payment lines (${money(totalPaidAmount)}) exceed down payment (${money(effectiveDownPayment)}).`;
    }
    return null;
  }, [isInstallmentSale, effectiveDownPayment, totalPaidAmount]);

  const isFullPaid = useMemo(() => {
    if (isInstallmentSale) {
      return effectiveSaleTotal > 0 && effectiveDownPayment >= effectiveSaleTotal - 0.01;
    }
    return effectiveSaleTotal > 0 && totalPaidAmount >= effectiveSaleTotal - 0.01;
  }, [isInstallmentSale, effectiveSaleTotal, totalPaidAmount, effectiveDownPayment]);

  const rebuildInstallmentPlan = (resetManual = true) => {
    if (!isInstallmentSale || financedAmount <= 0 || !installmentFirstDueDate.trim()) {
      setInstallmentRows([]);
      return;
    }
    setInstallmentRows(
      buildInstallmentPlan(financedAmount, installmentMonths, installmentFirstDueDate, installmentFrequency),
    );
    if (resetManual) {
      installmentsManuallyEditedRef.current = false;
    }
  };

  useEffect(() => {
    if (!isInstallmentSale) {
      setInstallmentRows([]);
      installmentsManuallyEditedRef.current = false;
      return;
    }
    rebuildInstallmentPlan(true);
  }, [isInstallmentSale, installmentMonths, installmentFirstDueDate, installmentFrequency]);

  useEffect(() => {
    if (!isInstallmentSale || installmentsManuallyEditedRef.current) return;
    rebuildInstallmentPlan(false);
  }, [financedAmount]);

  useEffect(() => {
    if (effectiveBalance <= 0 && salePaymentType === "installment") {
      return;
    }
    if (effectiveBalance <= 0 && installmentEnabled) {
      setInstallmentEnabled(false);
    }
  }, [effectiveBalance, installmentEnabled, salePaymentType]);

  const handleInstallmentAmountChange = (installmentNo: number, amount: number) => {
    installmentsManuallyEditedRef.current = true;
    setInstallmentRows((rows) =>
      rows.map((row) =>
        row.installment_no === installmentNo
          ? { ...row, amount: Math.round(Math.max(0, amount) * 100) / 100 }
          : row,
      ),
    );
  };

  const handleInstallmentDueDateChange = (installmentNo: number, dueDate: string) => {
    setInstallmentRows((rows) =>
      rows.map((row) =>
        row.installment_no === installmentNo
          ? { ...row, due_date: dueDate }
          : row,
      ),
    );
  };

  const handleInstallmentMonthsChange = (count: number) => {
    installmentsManuallyEditedRef.current = false;
    setInstallmentMonths(count);
  };

  const handleInstallmentFrequencyChange = (freq: InstallmentFrequency) => {
    installmentsManuallyEditedRef.current = false;
    setInstallmentFrequency(freq);
  };

  const handleCancel = () => navigate("/product-sales");

  const recordSale = async () => {
    if (saleSaving) return;
    const productIdNum = Number(selectedProduct?.id);
    const customerIdNum = Number(saleCustomerId);
    const employeeIdNum = Number(saleEmployeeId);
    const total = effectiveSaleTotal;

    if (!isEdit && (!Number.isFinite(productIdNum) || productIdNum <= 0)) {
      toast.error("Select a product.");
      return;
    }
    if (!Number.isFinite(customerIdNum) || customerIdNum <= 0) {
      toast.error("Select a customer.");
      return;
    }
    if (!Number.isFinite(total) || total <= 0) {
      toast.error("Enter a valid sale price.");
      return;
    }
    if (totalPaidAmount > total) {
      toast.error("Payment amount cannot exceed sale price.");
      return;
    }

    if (!isEdit && isInstallmentSale) {
      if (effectiveSaleTotal <= 0) {
        toast.error("Enter a valid sale price.");
        return;
      }
      if (downPaymentPaymentError) {
        toast.error(downPaymentPaymentError);
        return;
      }
      if (financedAmount > 0) {
        if (!installmentFirstDueDate.trim()) {
          toast.error("Select the first installment due date.");
          return;
        }
        if (installmentRows.length === 0) {
          toast.error("Configure a valid installment schedule.");
          return;
        }
        if (installmentRows.some((row) => row.amount <= 0)) {
          toast.error("Each installment amount must be greater than zero.");
          return;
        }
        if (!installmentsMatchBalance(installmentRows, financedAmount, 0.02)) {
          toast.error(`Installment total must match the financed amount (${money(financedAmount)}).`);
          return;
        }
      }
      if (latePenaltyType !== "none") {
        const penaltyVal = Number(latePenaltyValue);
        if (!Number.isFinite(penaltyVal) || penaltyVal <= 0) {
          toast.error("Enter a valid late payment penalty value greater than zero.");
          return;
        }
        if (latePenaltyType === "percentage" && penaltyVal > 100) {
          toast.error("Late payment penalty percentage cannot exceed 100%.");
          return;
        }
      }
    }

    let deliveryDate: string | null = null;
    if (isFullPaid && gatePassEnabled) {
      deliveryDate = formatDeliveryPayload(gatePassDate, gatePassTime);
      if (!deliveryDate) {
        toast.error("Delivery date and time are required for gate pass.");
        return;
      }
    }

    setSaleSaving(true);
    try {
      const paymentsPayload: SalePaymentLine[] = paymentEntries.map((p, idx) => ({
        clientId: p.id || String(idx + 1),
        paymentMethod: p.paymentMethod,
        amount: p.amount,
        bankId: p.bankId || "",
        bankName: p.bankName || "",
        transactionReferenceNo: p.transactionReferenceNo || "",
        payOrderNo: p.payOrderNo || "",
        chequeNo: p.chequeNo || "",
        depositSlipNo: p.depositSlipNo || "",
        paymentMode: p.paymentMode,
        notes: p.notes,
        paymentDate: p.paymentDate,
      }));

      const primary = paymentsPayload[0] ?? null;
      const payments = buildSalePaymentsPayload(paymentsPayload);

      const payload = {
        customer_id: customerIdNum,
        employee_id: Number.isFinite(employeeIdNum) && employeeIdNum > 0 ? employeeIdNum : null,
        date: saleDate.trim() || primaryPaymentEntry?.paymentDate || null,
        total_amount: total,
        token_amount: tokenAmount,
        token_type: tokenEntry?.tokenType || (tokenAmount > 0 ? saleTokenType : null),
        balance_amount: isInstallmentSale ? financedAmount : effectiveBalance,
        payable_date: isInstallmentSale
          ? null
          : tokenEntry?.payableDate || (tokenAmount > 0 ? (salePayableDate.trim() || null) : null),
        delivery_date: deliveryDate,
        payment_method: primary?.payment_method ?? null,
        bank_id: primary?.bank_id ?? null,
        transaction_reference_no: primary?.transaction_reference_no ?? null,
        pay_order_no: primary?.pay_order_no ?? null,
        payments,
        description: saleDescription.trim() || null,
      };

      if (isEdit && editId) {
        const { message } = await updateSale(editId, payload);
        toast.success(message);
        navigate("/product-sales");
        return;
      }

      const { message } = await createSale({
        product_id: productIdNum,
        ...payload,
        is_installment: isInstallmentSale,
        installment_count: isInstallmentSale ? installmentMonths : null,
        installment_first_due_date: isInstallmentSale ? installmentFirstDueDate.trim() || null : null,
        installment_frequency: isInstallmentSale ? installmentFrequency : null,
        installments: isInstallmentSale ? installmentRows : undefined,
        late_penalty_type: isInstallmentSale ? latePenaltyType : "none",
        late_penalty_value:
          isInstallmentSale && latePenaltyType !== "none"
            ? (Number(latePenaltyValue) || 0)
            : null,
      });
      toast.success(message);
      navigate("/product-sales");
    } catch (error) {
      toast.error(saleErrorMessage(error));
    } finally {
      setSaleSaving(false);
    }
  };

  const formBlocked = refsLoading || prefillLoading || (isEdit && !editHydrated);
  const pageTitle = isEdit ? "Edit sale" : "Record sale";
  const pageSubtitle = isEdit ? "Update sale details and payment information" : "Create a new product sale and invoice";

  if (!canSave) {
    return (
      <PermissionDenied
        title={isEdit ? "Cannot edit sale" : "Cannot record sale"}
        message={
          isEdit
            ? "You don’t have permission to edit sales. Ask an administrator to grant sale_can_update."
            : "You don’t have permission to record sales. Ask an administrator to grant sale_can_create."
        }
      />
    );
  }

  if (editLoadError) {
    return (
      <Layout title="Edit sale">
        <div className="glass-card p-8 space-y-4">
          <p className="text-sm text-muted-foreground">{editLoadError}</p>
          <Button type="button" variant="glass" onClick={handleCancel}>
            Back to sales
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={pageTitle}>
      <div className="w-full max-w-none space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
              onClick={handleCancel}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-display text-2xl font-bold text-foreground">{pageTitle}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{pageSubtitle}</p>
            </div>
          </div>
          <Button type="button" variant="glass" onClick={handleCancel} disabled={saleSaving}>
            Cancel
          </Button>
        </div>

        {formBlocked ? (
          <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : (
        <div className="space-y-2">
            <div className="glass-card p-6">
              <SectionHeader
                title="Sale information"
                subtitle="Product, sale price, customer, and salesperson"
                icon={Package}
              />
              <div className="space-y-6">
                <FieldGroup>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <div className="space-y-2 md:col-span-2 xl:col-span-4">
                      <RequiredLabel>Product</RequiredLabel>
                      <div className="min-w-0 space-y-3">
                        {isEdit ? (
                          <Input
                            className="glass-input h-10 bg-muted/30"
                            readOnly
                            value={selectedProduct?.name ?? "—"}
                          />
                        ) : prefillLoading ? (
                          <div className="flex h-10 items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading product…
                          </div>
                        ) : (
                          <SaleProductPicker
                            value={selectedProduct}
                            onChange={handleProductChange}
                            disabled={saleSaving}
                          />
                        )}
                        {selectedProduct || productDealLoading ? (
                          <ProductDealSummary
                            data={productDealLoading ? productDeal : (productDeal ?? {})}
                            loading={productDealLoading}
                          />
                        ) : null}
                      </div>
                    </div>
                    <MoneyAmountField
                      label="Sale price"
                      required
                      id="sale-price"
                      value={effectiveSaleTotal}
                      disabled={saleSaving}
                      onChange={(amount) => setSaleTotal(amount > 0 ? String(amount) : "")}
                    />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between min-h-6">
                        <RequiredLabel>Sale date</RequiredLabel>
                      </div>
                      <DatePartsPicker
                        value={saleDate}
                        onChange={setSaleDate}
                        disabled={saleSaving}
                        placeholder="Select sale date"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between min-h-6 gap-2">
                        <RequiredLabel>Customer</RequiredLabel>
                        {canCreateCustomer && !isEdit ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 text-primary hover:text-primary hover:bg-primary/10"
                            aria-label="Create customer"
                            title="Create customer"
                            onClick={() => setCreateCustomerOpen(true)}
                            disabled={saleSaving || refsLoading}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </Button>
                        ) : null}
                      </div>
                      <Select
                        value={saleCustomerId || "__none__"}
                        onValueChange={(v) => setSaleCustomerId(v === "__none__" ? "" : v)}
                        disabled={refsLoading || saleSaving}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder={refsLoading ? "Loading…" : "Select customer"} />
                        </SelectTrigger>
                        <SelectContent position="popper">
                          <SelectItem value="__none__">Select…</SelectItem>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {formatCustomerDisplayName(c)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between min-h-6">
                        <Label className="text-xs text-muted-foreground">Salesperson</Label>
                      </div>
                      <Select
                        value={saleEmployeeId || "__none__"}
                        onValueChange={(v) => setSaleEmployeeId(v === "__none__" ? "" : v)}
                        disabled={refsLoading || saleSaving}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder={refsLoading ? "Loading…" : "Optional"} />
                        </SelectTrigger>
                        <SelectContent position="popper">
                          <SelectItem value="__none__">None (optional)</SelectItem>
                          {employees.map((e) => (
                            <SelectItem key={e.id} value={String(e.id)}>
                              {e.fullName || `${e.firstName} ${e.lastName}`.trim() || e.employeeCode || `Employee #${e.id}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between min-h-6">
                        <Label className="text-xs text-muted-foreground">Payment type</Label>
                      </div>
                      <Select
                        value={salePaymentType}
                        onValueChange={(v) => {
                          const next = v === "installment" ? "installment" : "cash";
                          setSalePaymentType(next);
                          if (next === "cash") {
                            setInstallmentEnabled(false);
                            setInstallmentRows([]);
                            setDownPayment(0);
                            setInstallmentFrequency("monthly");
                            installmentsManuallyEditedRef.current = false;
                            setPaymentEntries((prev) =>
                              prev.map((p) =>
                                p.paymentMode === "installment"
                                  ? { ...p, paymentMode: "partial", installmentMonths: undefined, installmentFirstDueDate: undefined }
                                  : p,
                              ),
                            );
                          } else {
                            setInstallmentEnabled(true);
                          }
                        }}
                        disabled={saleSaving || isEdit}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="Select payment type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="installment">Installment</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </FieldGroup>
              </div>
            </div>

            <div className="space-y-3">
              {/* Installment Plan Schedule if active */}
              {!isEdit && isInstallmentSale ? (
                <SaleInstallmentPlanner
                  enabled={true}
                  hideToggle={true}
                  onEnabledChange={setInstallmentEnabled}
                  totalSalePrice={effectiveSaleTotal}
                  downPayment={downPayment}
                  onDownPaymentChange={(amt) => {
                    setDownPayment(amt);
                    installmentsManuallyEditedRef.current = false;
                  }}
                  tokenAmount={tokenAmount}
                  balanceAmount={financedAmount}
                  saleDate={saleDate}
                  payableDate={salePayableDate}
                  monthCount={installmentMonths}
                  onMonthCountChange={handleInstallmentMonthsChange}
                  firstDueDate={installmentFirstDueDate}
                  onFirstDueDateChange={setInstallmentFirstDueDate}
                  frequency={installmentFrequency}
                  onFrequencyChange={handleInstallmentFrequencyChange}
                  installments={installmentRows}
                  onInstallmentAmountChange={handleInstallmentAmountChange}
                  onInstallmentDueDateChange={handleInstallmentDueDateChange}
                  latePenaltyType={latePenaltyType}
                  onLatePenaltyTypeChange={setLatePenaltyType}
                  latePenaltyValue={latePenaltyValue}
                  onLatePenaltyValueChange={setLatePenaltyValue}
                  disabled={saleSaving}
                />
              ) : null}

              <div className="rounded-lg border border-border/50 bg-background/60 p-3.5 space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 pb-2.5">
                  <div>
                    <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                      <span>{isInstallmentSale ? "Down payment receipt lines" : "Sale payment lines"}</span>
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {isInstallmentSale
                        ? effectiveDownPayment > 0
                          ? tokenAmount > 0
                            ? `Down payment: ${money(effectiveDownPayment)} (Token: ${money(tokenAmount)}, Net due today: ${money(Math.max(0, effectiveDownPayment - tokenAmount))}). Record receipt method(s) (Cash, Bank, Cheque).`
                            : `Record payment method(s) (Cash, Bank, Cheque) for the down payment of ${money(effectiveDownPayment)}.`
                          : "No down payment entered. 100% of the sale price is financed into monthly installments."
                        : "Record token, advance, full, or partial payment lines against this sale."}
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="gap-1.5 shrink-0 h-8 font-medium shadow-xs"
                    onClick={() => {
                      setEditingPaymentEntry(null);
                      setPaymentModalOpen(true);
                    }}
                    disabled={
                      saleSaving ||
                      !saleCustomerId ||
                      effectiveSaleTotal <= 0 ||
                      (isInstallmentSale
                        ? effectiveDownPayment <= 0 ||
                          totalPaidAmount >= effectiveDownPayment - 0.009
                        : effectiveSaleTotal > 0 && effectiveBalance <= 0 && paymentEntries.length > 0)
                    }
                    title={
                      !saleCustomerId && effectiveSaleTotal <= 0
                        ? "Please select a customer and enter a sale price first"
                        : !saleCustomerId
                        ? "Please select a customer first"
                        : effectiveSaleTotal <= 0
                        ? "Please enter a sale price first"
                        : isInstallmentSale && effectiveDownPayment <= 0
                        ? "Enter a down payment greater than 0 above to record an upfront payment."
                        : isInstallmentSale && totalPaidAmount >= effectiveDownPayment - 0.009
                        ? "Down payment is already covered by the payment lines."
                        : effectiveSaleTotal > 0 && effectiveBalance <= 0 && paymentEntries.length > 0
                        ? "Sale is fully paid. Remaining balance is 0."
                        : undefined
                    }
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add payment
                  </Button>
                </div>

                {paymentEntries.length === 0 ? (
                  <div className="text-xs text-muted-foreground rounded-lg bg-muted/20 border border-border/40 p-4 text-center">
                    <p className="font-medium text-foreground/80">No payment lines added.</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {!saleCustomerId || effectiveSaleTotal <= 0
                        ? "Please select a customer and enter a sale price above to enable adding payments."
                        : isInstallmentSale
                          ? effectiveDownPayment > 0
                            ? `Click "+ Add payment" to record payment method for the down payment (${money(effectiveDownPayment)}).`
                            : "No down payment required. All installments will be collected later from the invoice."
                          : "Click \"+ Add payment\" to record token, advance, or full payment."}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border/40 rounded-lg border border-border/40 bg-card overflow-hidden">
                    {paymentEntries.map((line, i) => {
                      const methodLabel = line.paymentMethod.toUpperCase();
                      const modeLabel =
                        line.paymentMode === "full"
                          ? "Full Payment (100%)"
                          : line.paymentMode === "installment"
                          ? "Installment Down Payment"
                          : line.paymentMode === "partial"
                          ? "Partial Payment"
                          : "Token / Advance";

                      return (
                        <div
                          key={line.id || i}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 text-xs hover:bg-muted/10 transition-colors"
                        >
                          <div className="space-y-1 min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-bold text-foreground text-sm">Payment #{i + 1}</span>
                              <span
                                className={cn(
                                  "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border",
                                  line.paymentMode === "full"
                                    ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                                    : line.paymentMode === "installment"
                                    ? "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20"
                                    : line.paymentMode === "partial"
                                    ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"
                                    : "bg-primary/10 text-primary border-primary/20"
                                )}
                              >
                                {modeLabel}
                              </span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-muted text-foreground border border-border/60">
                                {methodLabel}
                              </span>
                              {line.bankName && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                                  Bank: {line.bankName}
                                </span>
                              )}
                            </div>

                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                              <span>
                                Date: <strong className="text-foreground">{line.paymentDate}</strong>
                              </span>
                              {line.chequeNo && (
                                <span>
                                  Cheque: <strong className="text-foreground">#{line.chequeNo}</strong>
                                </span>
                              )}
                              {line.transactionReferenceNo && (
                                <span>
                                  Txn Ref: <strong className="text-foreground">{line.transactionReferenceNo}</strong>
                                </span>
                              )}
                              {line.payOrderNo && (
                                <span>
                                  PO No: <strong className="text-foreground">{line.payOrderNo}</strong>
                                </span>
                              )}
                              {line.depositSlipNo && (
                                <span>
                                  Slip No: <strong className="text-foreground">{line.depositSlipNo}</strong>
                                </span>
                              )}
                              {line.payableDate && (
                                <span>
                                  Due Date: <strong className="text-amber-600 dark:text-amber-400">{line.payableDate}</strong>
                                  {line.tokenType ? ` (${line.tokenType})` : ""}
                                </span>
                              )}
                              {line.installmentMonths && (
                                <span>
                                  Plan: <strong className="text-purple-600 dark:text-purple-400">{line.installmentMonths} Months</strong>
                                </span>
                              )}
                              {line.notes && (
                                <span className="italic truncate max-w-xs">&quot;{line.notes}&quot;</span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-border/30">
                            <div className="text-right">
                              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                Amount Received
                              </p>
                              <p className="font-bold text-sm tabular-nums text-primary">
                                {formatCurrency(line.amount)}
                              </p>
                            </div>

                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                                onClick={() => {
                                  setEditingPaymentEntry(line);
                                  setPaymentModalOpen(true);
                                }}
                                disabled={saleSaving}
                                aria-label="Edit payment"
                                title="Edit payment line"
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive/80 hover:text-destructive hover:bg-destructive/10"
                                onClick={() => setPendingPaymentRemoveIndex(i)}
                                disabled={saleSaving}
                                aria-label="Remove payment"
                                title="Remove payment line"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {downPaymentPaymentError ? (
                  <p className="text-sm text-destructive pt-1">{downPaymentPaymentError}</p>
                ) : null}
              </div>

              {/* Summary Subsection ONLY for Cash Sales (Installment sales display overview inside planner) */}
              {!isInstallmentSale ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 pt-1">
                  <div className="rounded-lg border border-border/60 bg-background/80 px-3 py-2 space-y-1">
                    <div className="flex items-center justify-between min-h-[1.25rem]">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">
                        Total Sale Price
                      </span>
                      {pkrAmountShortLabel(effectiveSaleTotal) && (
                        <span className="text-[10px] font-semibold text-muted-foreground">
                          {pkrAmountShortLabel(effectiveSaleTotal)}
                        </span>
                      )}
                    </div>
                    <div className="h-8 flex items-center justify-end">
                      <p className="text-sm sm:text-base font-bold font-display text-foreground leading-none text-right tabular-nums">
                        {formatCurrency(effectiveSaleTotal)}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 space-y-1">
                    <div className="flex items-center justify-between min-h-[1.25rem]">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-primary">
                        Total Received / Paid
                      </span>
                      {pkrAmountShortLabel(totalPaidAmount) && (
                        <span className="text-[10px] font-semibold text-primary/90">
                          {pkrAmountShortLabel(totalPaidAmount)}
                        </span>
                      )}
                    </div>
                    <div className="h-8 flex items-center justify-end">
                      <p className="text-sm sm:text-base font-bold font-display text-primary leading-none text-right tabular-nums">
                        {formatCurrency(totalPaidAmount)}
                      </p>
                    </div>
                  </div>

                  <div
                    className={cn(
                      "rounded-lg border px-3 py-2 space-y-1",
                      effectiveBalance > 0
                        ? "border-amber-500/40 bg-amber-500/5"
                        : "border-emerald-500/40 bg-emerald-500/5",
                    )}
                  >
                    <div className="flex items-center justify-between min-h-[1.25rem]">
                      <span
                        className={cn(
                          "text-[9px] font-bold uppercase tracking-wider",
                          effectiveBalance > 0
                            ? "text-amber-700 dark:text-amber-300"
                            : "text-emerald-700 dark:text-emerald-300",
                        )}
                      >
                        Remaining Balance
                      </span>
                      {effectiveBalance > 0 && pkrAmountShortLabel(effectiveBalance) ? (
                        <span className="text-[10px] font-semibold text-amber-700/90 dark:text-amber-300/90">
                          {pkrAmountShortLabel(effectiveBalance)}
                        </span>
                      ) : effectiveBalance <= 0 && effectiveSaleTotal > 0 ? (
                        <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                          Fully Settled
                        </span>
                      ) : null}
                    </div>
                    <div className="h-8 flex items-center justify-end">
                      <p
                        className={cn(
                          "text-sm sm:text-base font-bold font-display tabular-nums leading-none text-right",
                          effectiveBalance > 0
                            ? "text-amber-700 dark:text-amber-300"
                            : "text-emerald-700 dark:text-emerald-400",
                        )}
                      >
                        {formatCurrency(effectiveBalance)}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Gate Pass Option for Full Payment */}
              {isFullPaid ? (
                <div className="mt-4">
                  <GatePassOptionCard
                    enabled={gatePassEnabled}
                    onEnabledChange={setGatePassEnabled}
                    date={gatePassDate}
                    time={gatePassTime}
                    onDateChange={setGatePassDate}
                    onTimeChange={setGatePassTime}
                    currentYear={currentYear}
                    disabled={saleSaving}
                  />
                </div>
              ) : null}
            </div>

            <div className="glass-card p-6">
              <SectionHeader title="Notes" subtitle="Optional description for this sale" icon={FileText} />
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Description</Label>
                <Textarea
                  className="glass-input min-h-[100px] resize-y"
                  value={saleDescription}
                  onChange={(e) => setSaleDescription(e.target.value)}
                  placeholder="Optional note"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-border/40 pb-4 pt-4 sm:flex-row sm:items-center sm:justify-end">
              <Button
                type="button"
                variant="premium"
                className="w-full gap-2 px-8 sm:w-auto"
                onClick={() => void recordSale()}
              >
                {saleSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isEdit ? "Save changes" : "Save sale"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfigureSalePaymentDialog
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        salePrice={isInstallmentSale ? effectiveDownPayment : effectiveSaleTotal}
        remainingBalance={
          isInstallmentSale
            ? Math.max(0, Math.round((effectiveDownPayment - totalPaidAmount) * 100) / 100)
            : effectiveBalance
        }
        customerName={customers.find((c) => String(c.id) === saleCustomerId) ? formatCustomerDisplayName(customers.find((c) => String(c.id) === saleCustomerId)!) : undefined}
        productName={selectedProduct?.name}
        editingEntry={editingPaymentEntry}
        disabled={saleSaving}
        allowInstallmentMode={false}
        isInstallmentSale={isInstallmentSale}
        onSave={handleSavePaymentEntry}
      />

      <ConfirmAlertDialog
        open={pendingPaymentRemoveIndex !== null}
        onOpenChange={(open) => !open && setPendingPaymentRemoveIndex(null)}
        title="Remove payment line?"
        description="This payment line will be removed from the sale. Remaining balance will adjust automatically."
        cancelLabel="Keep"
        confirmLabel="Remove"
        onConfirm={() => {
          if (pendingPaymentRemoveIndex !== null) {
            handleRemovePaymentEntry(pendingPaymentRemoveIndex);
          }
        }}
      />

      <CreateCustomerDialog
        open={createCustomerOpen}
        onOpenChange={setCreateCustomerOpen}
        mode="full"
        title="Add customer"
        onCreated={async (record) => {
          await syncCustomersFromApi();
          setCustomerVersion((v) => v + 1);
          setSaleCustomerId(String(record.id));
        }}
      />
    </Layout>
  );
};

export default CreateSale;
